import {
  SITE_URL,
  SITE_NAME,
  SITE_LEGAL_NAME,
  SITE_DEFAULT_DESCRIPTION,
  WHATSAPP_NUMBER_E164,
  WHATSAPP_DISPLAY,
  CONTACT_EMAIL,
  SOCIAL,
  OFFICES,
  SITE_OG_IMAGE,
} from '../config/site';
import { faqs } from '../data/faqs';
import type { Service } from '../data/services';
import type { BlogPost } from '@educa-legal/shared';

const fullUrl = (path: string) =>
  path.startsWith('http') ? path : `${SITE_URL.replace(/\/$/, '')}${path.startsWith('/') ? '' : '/'}${path}`;

export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'LegalService',
    '@id': `${SITE_URL}#organization`,
    name: SITE_NAME,
    legalName: SITE_LEGAL_NAME,
    url: SITE_URL,
    logo: fullUrl('/logo-on-white.png'),
    image: fullUrl(SITE_OG_IMAGE),
    description: SITE_DEFAULT_DESCRIPTION,
    email: CONTACT_EMAIL,
    telephone: WHATSAPP_NUMBER_E164,
    areaServed: { '@type': 'Country', name: 'Brasil' },
    sameAs: [SOCIAL.instagram, SOCIAL.linkedin],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: WHATSAPP_NUMBER_E164,
        contactType: 'customer service',
        contactOption: 'TollFree',
        availableLanguage: ['Portuguese'],
        areaServed: 'BR',
      },
    ],
  };
}

export function localBusinessSchema() {
  return OFFICES.map((office, i) => ({
    '@context': 'https://schema.org',
    '@type': 'LegalService',
    '@id': `${SITE_URL}#office-${i}`,
    name: `${SITE_NAME} — ${office.city}`,
    parentOrganization: { '@id': `${SITE_URL}#organization` },
    url: SITE_URL,
    telephone: WHATSAPP_NUMBER_E164,
    email: CONTACT_EMAIL,
    address: {
      '@type': 'PostalAddress',
      streetAddress: office.streetAddress,
      addressLocality: office.addressLocality,
      addressRegion: office.addressRegion,
      addressCountry: office.addressCountry,
    },
  }));
}

export function personSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${SITE_URL}#pedro`,
    name: 'Pedro Guimarães Neto',
    jobTitle: 'Fundador e advogado especialista em direito educacional',
    worksFor: { '@id': `${SITE_URL}#organization` },
    image: fullUrl('/foto-perfil-pedro.webp'),
    description:
      'Fundador da Educa Legal. Advogado e contador com MBA em Direito Tributário e mais de 20 anos de trajetória no direito, dos quais 11 dedicados ao setor educacional.',
  };
}

export function faqPageSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };
}

export function serviceSchema(service: Service) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.title,
    serviceType: service.title,
    provider: { '@id': `${SITE_URL}#organization` },
    areaServed: { '@type': 'Country', name: 'Brasil' },
    description: service.metaDescription,
    url: fullUrl(`/servicos/${service.slug}`),
    audience: { '@type': 'EducationalAudience', educationalRole: 'school administrator' },
  };
}

export function breadcrumbSchema(items: { name: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: fullUrl(item.path),
    })),
  };
}

export function articleSchema(post: BlogPost) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    image: post.coverImage ? fullUrl(post.coverImage) : fullUrl(SITE_OG_IMAGE),
    datePublished: post.publishedAt,
    dateModified: post.updatedAt ?? post.publishedAt,
    author: { '@type': 'Person', name: post.author },
    publisher: { '@id': `${SITE_URL}#organization` },
    mainEntityOfPage: fullUrl(`/blog/${post.slug}`),
    keywords: post.tags.join(', '),
  };
}

export function blogIndexSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: `${SITE_NAME} — Blog`,
    url: fullUrl('/blog'),
    publisher: { '@id': `${SITE_URL}#organization` },
    description: 'Inteligência jurídica para o setor educacional brasileiro.',
  };
}

/** Renderiza o JSON-LD como string segura para `<script type="application/ld+json">`. */
export function jsonLd(obj: unknown): string {
  return JSON.stringify(obj).replace(/</g, '\\u003c');
}

export { WHATSAPP_DISPLAY, CONTACT_EMAIL };
